import {
  isJobActive,
  type BulkRunCellState,
  type BulkRunDetail,
  type BulkRunResource,
  type JobKind,
  type JobState,
} from '@studio/shared';
import { useState, type ReactNode } from 'react';
// The words a Bulk Run is read in live with the Bulk Run surfaces. The indicator shows
// the same run, so it borrows the same words rather than growing a second vocabulary
// that could drift from them.
import { cellStateLabel, progressLine, runStateLabel, spendLine } from '../../bulk/matrix.js';
import { api } from '../api.js';
import { Link } from '../router.js';
import { useAsync } from '../useAsync.js';
import type { ActivityEntry, ActivityState } from './reconcile.js';

/**
 * The one compact place outstanding work is visible from every screen. It is silent
 * whenever there is none: no count, no animation, no element at all. Work that has
 * stopped belongs on the slot that owns it, where the operator can act on it; an
 * indicator that is always present teaches them to stop looking at it.
 *
 * Two things count as outstanding, not one. Jobs in flight are the obvious half. The
 * other is a Bulk Run that is not over - paused, or waiting on an allowance - which by
 * definition has no Job in flight at all, and which R177 names first. Without it a run
 * would vanish from every screen the moment it was paused, and the state R177 asks the
 * activity detail to show would be the one state it could never show.
 *
 * The count opens the detail. R177 asks for the run-level state, ready out of total,
 * spend against the ceiling, rows by App and the five cell states to be readable from
 * here and not only from the Bulk Run surface - and asks for no finishing time, which
 * is why there is nowhere on this panel to put one.
 */
export function ActivityIndicator({
  activity,
  runs = [],
}: {
  activity: ActivityState;
  /** Bulk Runs that are not over. The shell reads them; this only shows them. */
  runs?: readonly BulkRunResource[];
}): ReactNode {
  const [open, setOpen] = useState(false);
  const busy = activity.activeCount > 0;
  if (!busy && runs.length === 0) return null;

  const working = activity.jobs.filter((entry) => isJobActive(entry.state));
  const label = busy
    ? `${activity.activeCount} в работе`
    : runs.length === 1
      ? `${runs.length} Bulk Run не завершён`
      : `${runs.length} Bulk Runs не завершены`;

  return (
    <div
      className={busy ? 'activity-indicator activity-busy' : 'activity-indicator activity-waiting'}
      aria-live="polite"
      aria-busy={busy}
    >
      {busy ? <span className="activity-pulse" aria-hidden="true" /> : null}
      <button type="button" className="activity-count" aria-expanded={open} onClick={() => setOpen((shown) => !shown)}>
        {label}
      </button>
      {!open ? null : (
        <div className="activity-detail">
          <ul aria-label="Текущая работа">
            {working.map((entry) => (
              <li key={entry.id}>
                <WorkLine entry={entry} />
              </li>
            ))}
          </ul>
          {runs.map((run) => (
            <RunGlance key={run.id} bulkRunId={run.id} />
          ))}
        </div>
      )}
    </div>
  );
}

const KIND_LABEL: Record<JobKind, string> = {
  'scan.inventory': 'Сканирование Store Listing в Google Play',
  'ingest.reference': 'Загрузка референса',
  'generate.candidate': 'Генерация Candidate',
  'render.animatic': 'Рендер Animatic',
  'render.video': 'Рендер видео',
  'transform.image': 'Подготовка превью',
  'check.technical': 'Запуск Technical Check',
  'package.export': 'Сборка Export Package',
  'extract.evidence': 'Чтение Outcome Evidence',
  'extract.learning': 'Извлечение Creative Learning',
  'bulk.cell': 'Строка Bulk Run',
};

const JOB_STATE_LABEL: Record<JobState, string> = {
  queued: 'В очереди',
  working: 'В работе',
  succeeded: 'Готово',
  failed: 'Остановлено',
  needsAttention: 'Нужно внимание',
  cancelled: 'Отменено',
};

/**
 * One line of work, and the way to whatever owns it.
 *
 * A Job the stream has named but no snapshot has described yet says so rather than
 * pretending to know what it is: the stream carries a version and a state, never a kind.
 */
function WorkLine({ entry }: { entry: ActivityEntry }): ReactNode {
  const job = entry.job;
  if (job === null) return <span>{`Фоновая работа · ${JOB_STATE_LABEL[entry.state]}`}</span>;

  return (
    <>
      <span>{`${KIND_LABEL[job.kind]} · ${JOB_STATE_LABEL[entry.state]}`}</span>
      {job.bulkRunId !== null ? null : job.experimentId === null ? null : (
        <Link to={`/experiments/${job.experimentId}/explore`}>Открыть Experiment</Link>
      )}
    </>
  );
}

const CELL_STATES: readonly BulkRunCellState[] = ['queued', 'working', 'ready', 'needsAttention', 'stopped'];

/**
 * One unfinished Bulk Run, read from where the operator noticed it.
 *
 * The rows and the five cell states are not on the run resource the shell already has,
 * so the detail is read when the panel is opened and not before. Everything here is a
 * count of something that has already happened: there is no finishing time on this
 * panel and no field for one.
 */
function RunGlance({ bulkRunId }: { bulkRunId: string }): ReactNode {
  const loaded = useAsync<BulkRunDetail>(() => api.readBulkRun(bulkRunId), [bulkRunId]);
  const detail = loaded.value;
  if (detail === null) return null;

  const { run, cells, byApp } = detail;
  const counted = CELL_STATES.map((state) => ({ state, count: cells.filter((cell) => cell.state === state).length }));

  return (
    <section className="activity-run" aria-label="Детали Bulk Run">
      <p>{`${runStateLabel(run.state)} · ${progressLine(run.readyCount, run.cellCount)} · ${spendLine(run.spentUsd, run.ceilingUsd)}`}</p>
      <ul>
        {byApp.map((group) => (
          <li key={group.appId}>{`${group.appName} · ${progressLine(group.readyCount, group.cellCount)}`}</li>
        ))}
      </ul>
      <ul>
        {counted.map((entry) => (
          <li key={entry.state}>{`${cellStateLabel(entry.state)}: ${entry.count}`}</li>
        ))}
      </ul>
      <Link to={`/bulk-runs/${run.id}`}>Открыть этот Bulk Run</Link>
    </section>
  );
}
